import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { SigningKey } from "../lib/types";
import { ConfirmDialog } from "../components/ConfirmDialog";
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  PageHeader,
  Table,
  TableWrap,
  Td,
  Th,
  cx,
  formatDate,
} from "../components/ui";

const STATUS_STYLE: Record<SigningKey["status"], string> = {
  active: "text-live",
  next: "text-accent",
  retired: "text-dim",
};

export function SigningKeys() {
  const queryClient = useQueryClient();
  const [kid, setKid] = useState(suggestKid());
  const [promoting, setPromoting] = useState<SigningKey | null>(null);

  const keys = useQuery({
    queryKey: ["signing-keys"],
    queryFn: () => api<SigningKey[]>("/api/v1/admin/signing-keys"),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["signing-keys"] });

  const generate = useMutation({
    mutationFn: () => api("/api/v1/admin/signing-keys", { method: "POST", body: { kid } }),
    onSuccess: () => {
      setKid(suggestKid());
      invalidate();
    },
  });

  const promote = useMutation({
    mutationFn: (input: { kid: string; confirm: string }) =>
      api(`/api/v1/admin/signing-keys/${input.kid}/promote`, {
        method: "POST",
        body: { confirm: input.confirm },
      }),
    onSuccess: () => {
      setPromoting(null);
      invalidate();
    },
  });

  const retire = useMutation({
    mutationFn: (key: string) =>
      api(`/api/v1/admin/signing-keys/${key}/retire`, { method: "POST", body: {} }),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Signing keys"
        lede="Every licence token is signed by the active key. Clients verify against the key id in the token, so retired keys stay published until the tokens they signed have expired."
      />

      <Card title="Rotate">
        <div className="space-y-4 p-4">
          {/* The order is the safety property: publish, wait for clients to
              pick it up, then promote. Stating it here keeps the next person
              from reaching for promote first. */}
          <ol className="space-y-1 text-sm text-muted">
            <li>1. Generate a key. It is published immediately but signs nothing.</li>
            <li>2. Ship an SDK release carrying the new key id.</li>
            <li>3. Promote it once deployments have that release.</li>
            <li>4. Retire the old key after the longest token lifetime has passed.</li>
          </ol>

          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              generate.mutate();
            }}
          >
            <div className="w-56">
              <Field label="New key id">
                <Input
                  value={kid}
                  required
                  className="tabular"
                  onChange={(event) => setKid(event.target.value)}
                />
              </Field>
            </div>
            <Button type="submit" variant="primary" disabled={generate.isPending}>
              {generate.isPending ? "Generating…" : "Generate key"}
            </Button>
          </form>

          {generate.isError && <ErrorNote>{(generate.error as Error).message}</ErrorNote>}
        </div>
      </Card>

      <Card title="Keys">
        {keys.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : (keys.data?.length ?? 0) === 0 ? (
          <EmptyState>
            No signing keys. The API cannot issue tokens until one is active.
          </EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Key id</Th>
                  <Th>Status</Th>
                  <Th>Created</Th>
                  <Th>Activated</Th>
                  <Th>Retired</Th>
                  <Th>{""}</Th>
                </tr>
              </thead>
              <tbody>
                {(keys.data ?? []).map((key) => (
                  <tr key={key.kid} className="border-b border-hairline last:border-0">
                    <Td label="Key id" className="tabular">
                      {key.kid}
                    </Td>
                    <Td label="Status" className={cx("font-medium", STATUS_STYLE[key.status])}>
                      {key.status}
                    </Td>
                    <Td label="Created" className="text-muted">
                      {formatDate(key.createdAt)}
                    </Td>
                    <Td label="Activated" className="text-muted">
                      {formatDate(key.activatedAt)}
                    </Td>
                    <Td label="Retired" className="text-muted">
                      {formatDate(key.retiredAt)}
                    </Td>
                    {/* Promote and Retire name themselves; the column never had
                        a header to carry down. */}
                    <Td>
                      <div className="flex gap-1">
                        {key.status === "next" && (
                          <Button variant="ghost" onClick={() => setPromoting(key)}>
                            Promote
                          </Button>
                        )}
                        {key.status === "active" && (
                          <Button
                            variant="ghost"
                            disabled={retire.isPending}
                            onClick={() => retire.mutate(key.kid)}
                          >
                            Retire
                          </Button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      <ConfirmDialog
        open={promoting !== null}
        title="Promote this signing key"
        confirmPhrase={promoting?.kid ?? ""}
        confirmLabel="Promote key"
        pending={promote.isPending}
        error={promote.isError ? (promote.error as Error).message : null}
        onCancel={() => setPromoting(null)}
        onConfirm={(typed) => {
          if (promoting !== null) promote.mutate({ kid: promoting.kid, confirm: typed });
        }}
        description={
          <>
            <p>
              Every token issued from now on is signed with this key. Any deployment whose SDK build
              does not carry this key id will reject those tokens.
            </p>
            <p>That takes every customer offline at once. Confirm the release has shipped first.</p>
          </>
        }
      />
    </div>
  );
}

/** `sf_<year>_<month>` matches the existing keys and sorts naturally. */
function suggestKid(): string {
  const now = new Date();
  return `sf_${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, "0")}`;
}
